
import { globals } from 'config/globals';
import { TimelineStateType } from 'app/views/home/timeline/timeline.types';
import { TimelineItemConfig } from 'app/model/TimelineItemConfig';
import { DepartmentType } from 'app/data/Enums';
import { device } from 'utils/device';

//Special partcile events
import { RelativeTimelinePosition } from 'app/views/home/timeline/RelativeTimelinePosition';
import { TimelineItemAttract } from 'app/views/home/timeline/itemMotion/TimelineItemAttract';
import { TimelineItemRepell } from 'app/views/home/timeline/itemMotion/TimelineItemRepell';

import { geometry, Point, Vector2 } from 'app/contracts/Geometry';
import { particles } from 'app/contracts/Particles';
import Config from 'config/settings';
import ParticleHitTests from 'app/views/home/targets/ParticleHitTests';
import ParticleTargets from 'app/views/home/targets/ParticleTargets';
import ParallaxItemTargets from 'app/views/home/targets/ParallaxItemTargets';
import webglHome from 'config/webgl-home';

export interface TimelineItemSettings {
	container: PIXI.Container;
	texture?: PIXI.Texture;
	textureMobile?: PIXI.Texture;
	backgroundTexture?: PIXI.Texture;
	maskTexture?: PIXI.Texture;
	textures?: Array<PIXI.Texture> | { [key: string]: PIXI.Texture };
	particleCount?: number;
	logoPointPosition?: Array<Vector2>;
}

export enum TimelineItemType {
	PARTICLE,
	PARTICLE_FIELD,
	RELATIVE_CHILDREN
}

//Item on the timeline
//Can be a particle of something
//Will be position relatively across the
//So an item at 0.5 / 0.5 will be in the center middle
export class TimelineItem {
	public department: DepartmentType;
	public type: TimelineItemType = TimelineItemType.PARTICLE;
	public id: string;
	//Named mask region this item's bounds contribute to (TimelineItemConfig
	//.maskRegion; undefined = none) — HomeTimeline._getItemsByMaskRegion keys
	//off it for HomeItemGeometry.getMaskRegionItems:
	public maskRegion: string;
	public relativeX: number;
	public relativeY: number;
	public scale: number = 1; //Scale when fully visible
	public easePower: number = 10;

	public parallaxOffsetPower: number = 0;
	private _parallaxOffsetBase: number = 0.5;
	public parallaxPowerMouse: number = 0.2; //Ratio to effecy height by
	public tint: number;
	public alpha: number;
	public textureName: string; //Set externally in HomeTimeline._createItem

	private _easePowers: any = {};
	protected _x: number = 0; //X current value
	private _targetPosition: RelativeTimelinePosition;
	protected _absoluteY: number; //Translated Y to px values
	protected _dragOffsetX: number = 0; //The amount the item is moved on X because or dragging inernia or being repelled by
	protected _offsetY: number = 0; //The amount the item is moved on Y because or dragging inernia or being repelled by
	private _offsetX: number = 0;
	private _yAnimate: number = 0;
	private _yAnimateDirection: number = 1;
	protected _offsetTargetX: number = 0; //The target the x offset is moving towaards
	protected _offsetTargetY: number = 0; //The target the y offset is moving towaards

	protected _mouseOffsetX: number = 0;
	protected _mouseOffsetTargetX: number = 0;

	protected _containerHeight: number;
	protected _containerWidth: number;
	private _innerWidth: number;

	//Parallax calulations:
	private _relativeParallaxX: number;
	private _relativeParallaxXToProgressRatio: number;

	private _parallaxEaseMultipler: number = 1;

	protected _scale: number = 1;
	protected _scaleTarget: number = 1;
	protected _displayScale: number = 1;

	protected _rotationTarget: number = 0;
	protected _rotation: number = 0;

	private _alpha: number = 1;

	protected _sprite: PIXI.Sprite;

	private _tween: gsap.Tween;

	private _settings: TimelineItemSettings;
	private _json: TimelineItemConfig;
	private _zone: string = null;

	private _hasAutoEffect: boolean = false;
	private _effectRange: number = 200;
	private _startRangeAt: number = 0;
	private _autoStartRotation: number = 0;
	private _autoStartAlpha: number = 0;
	private _autoStartScale: number = 0.4;

	private _animatingToWork: boolean = false;
	private _finalScale: number = 1;
	private _finalRotation: number = 0;

	private _sceneProps: particles.SharedSceneContext; //Keep track, passed down to all required partciles/elements
	private _viewPortCount: number;

	protected _repellActive: boolean = false;
	private _repell: any = {
		homex: 0,
		homey: 0,
		forcex: 0,
		forcey: 0,
		magnet: 200,
		distancex: 0,
		distancey: 0,
		distance: 0,
		powerx: 0,
		powery: 0,
		moveX: 0,
		moveY: 0,

		//Moth
		magnetMoth: 200,
		distancexMoth: 0,
		distanceyMoth: 0,
		distanceMoth: 0,
		powerxMoth: 0,
		poweryMoth: 0,
		moveXMoth: 0,
		moveYMoth: 0
	};

	private _mouseRepellPowerDefault: number = 10;
	private _mouseRepelEasePower: number = 10;
	private _mouseRepellPower: number;


	private _particleAttract: TimelineItemAttract;
	private _particleAttractRepell: TimelineItemRepell;


	private _distanceTolerence: number = 400;

	private _hitTestable: boolean = false;
	private _targetable: boolean = false;
	private _spriteBindable: boolean = false;

	public set mouseRepellPower(v: number) {
		this._mouseRepellPowerDefault = v;
	}

	public set mouseRepelEasePower(v: number) {
		this._mouseRepelEasePower = v;
	}

	public get sprite(): PIXI.Sprite {
		return this._sprite;
	}

	public setupForWork() {
		this._sprite.visible = false;
	}

	public showForWork() {
		this._sprite.visible = true;
	}

	private _zoneCropXShift;
	private _zoneCropScaleShift;
	private _zoneCropHide;
	private _zoneYSizeShift;

	private _currentTexture: PIXI.Texture;

	public create(
		jsonConfig: TimelineItemConfig,
		settings: TimelineItemSettings,
		sceneProps: particles.SharedSceneContext = null
	): void {
		if ((window as any).smallLogo && jsonConfig.id === 'intro') {
			jsonConfig.relativeY = 0.7;
		}

		this._settings = settings;

		if (jsonConfig.zone) {
			this._zone = jsonConfig.zone;
			this._zoneCropXShift = jsonConfig.cropXShift;
			this._zoneCropScaleShift = jsonConfig.cropScaleShift;
			this._zoneCropHide = jsonConfig.hideAtCrop;
			this._zoneYSizeShift = jsonConfig.ySizeShift;
		}

		if (jsonConfig.parallaxOffsetBase || jsonConfig.parallaxOffsetBase === 0) {
			this._parallaxOffsetBase = jsonConfig.parallaxOffsetBase;
		}

		this.id = jsonConfig.id;
		this.maskRegion = jsonConfig.maskRegion;
		this._json = jsonConfig;
		this._sceneProps = sceneProps;


		this._hasAutoEffect = this._json.autoAlpha || this._json.autoRotate || this._json.autoScale;

		this._targetPosition = new RelativeTimelinePosition(jsonConfig.relativeX, jsonConfig.relativeY);

		if (jsonConfig.randomRotation) {
			this._finalRotation = (Math.random() * 120 * Math.PI) / 180;
			if (Math.random() < 0.5) {
				this._finalRotation *= -1;
			}
		}

		if (jsonConfig.minScale) {
			let range: number = jsonConfig.maxScale - jsonConfig.minScale;
			this._finalScale = jsonConfig.minScale + range * Math.random();
		}

		if (this._hasAutoEffect) {
			this._autoStartRotation =
				this._finalRotation + (this._json.startRotation * Math.PI) / 180 ||
				this._finalRotation + -1 * ((Math.random() * 30 * Math.PI) / 180);
			this._autoStartAlpha = this._json.startAlpha || 0;
			this._autoStartScale = this._json.startScale || 0.4;
			this._startRangeAt = this._json.startRangeAt || 0;
		}

		this._sprite = new PIXI.Sprite();
		this._sprite.cullable = true;

		this._sprite.interactive = false;
		this._sprite.anchor.set(0.5, 0.5);

		if (settings.texture) {
			this._currentTexture = this._getCurrentTexture();
			console.log(this._currentTexture);
			this._sprite.texture = this._currentTexture;
			this._sprite.name = this.textureName + ' RelX ' + jsonConfig.relativeX || 'no name';
		}

		settings.container.addChild(this._sprite);

		if (this._json.tint) {
			this._sprite.tint = this.tint;
		}

		if (this._json.alpha) {
			this._sprite.alpha =
				device.innerWidth < Config.mobileBreakpoint && this._json.alphaMobile ? this._json.alphaMobile : this.alpha;
		}

		if (jsonConfig.bioAlphaMax) {
		}

		let introActive: boolean = Math.random() < 0.15;
		let buttonActive: boolean = Math.random() < 0.25;

		if (jsonConfig.path) {
			//ParticleLamp (distance-to-mouse alpha) was never enabled here; the
			//class now lives at particles/behaviour/ParticleLamp. Original wiring:
			/*this._particleLamp = new ParticleLamp(
				this._sprite,
				100,
				600,
				//For hoz split:
				//0.4,
				//For vertical split:
				minAlpha,
				maxAlpha
			);
			*/
		}

		if (jsonConfig.path) {
			this._particleAttractRepell = new TimelineItemRepell(this._sprite);
			this._particleAttract = new TimelineItemAttract(this._sprite, null, null, this._particleAttractRepell);
			this._particleAttract.introActive = introActive;
			this._particleAttract.buttonActive = buttonActive;
		} else {
			if (this._json.repell) {
				this._repellActive = true;
			}
		}

		if (jsonConfig.maxScale || jsonConfig.minScale) {
			let minScale: number = jsonConfig.minScale || jsonConfig.maxScale || 1;
			let maxScale: number = jsonConfig.maxScale || 1;
			let range = maxScale - minScale;
			this._scaleTarget = this._scale = minScale + Math.random() * range;
		}

		//Set the department based on postion
		this.department = this.relativeX < 0.5 ? DepartmentType.SCREENS : DepartmentType.SPACES;

		0.8 + Math.random() * 0.9;

		this._mouseRepelEasePower = 10 + Math.random() * 30;
		this._distanceTolerence = 50 + Math.random() * 350;

		if (this.id === 'intro') {
			this._scale = 0.6;
			this._sprite.scale.set(this._scale, this._scale);
		}

		if (this._json.alphaMobile) {
			this._sprite.alpha =
				device.innerWidth < Config.mobileBreakpoint && this._json.alphaMobile ? this._json.alphaMobile : this.alpha;
		}

	}

	public fadeIn(delay: number, alpha: number = 1.0, time: number = 0.3) {
		gsap.TweenMax.to(this._sprite, time, { alpha: alpha, delay: delay, ease: Config.linearEase });
	}

	public setTint(tint) {
		gsap.TweenMax.to(this._sprite, 0.9, { pixi: { tint: tint }, ease: Config.linearEase });
	}

	//Resize event.
	//Pass in the made-up container size of the home partciles container
	//Also pass in displayScale to determine sizing as
	public resize(
		containerWidth: number,
		containerHeight: number,
		displayScale: number,
		innerwidth: number,
		positionScale: number,
		//Optional: legacy ParticleField/RelativeChildren call resize with 5 args, so
		//child items receive undefined here (pre-existing runtime behaviour):
		viewPortCount?: number
	) {
		//Resize the abs postion values in
		this._viewPortCount = viewPortCount;
		this._containerHeight = containerHeight;
		this._containerWidth = containerWidth;
		this._absoluteY = containerHeight * this.relativeY;

		this._targetPosition.resize(this._containerWidth, this._containerHeight);

		if (this._particleAttract) {
			this._particleAttract.resize(this._containerWidth, this._containerHeight);
		}

		this._displayScale = displayScale;

		if (this._sceneProps && this._sceneProps.zones && this._zone) {
			this._displayScale = this._sceneProps.zones[this._zone].scale;
		}

		this._hitTestable = false;
		if ((this.id === 'systems-block' || this.id === 'systems-block-move') && this.sprite.alpha > 0) {
			this._hitTestable = true;
		}

		if (this.id === 'systems-target') {
			this._targetable = true;
		}

		if (this.id === 'systems-grid') {
			this._spriteBindable = true;
		}

		if (this.id === 'intro' && device.innerWidth < 600) {
			this._displayScale = 0.38;
		}

		this._innerWidth = innerwidth;

		this._mouseRepellPower = this._mouseRepellPowerDefault;

		if (this._innerWidth < 500) {
		} else if (device.isSmallDevice) {
		}

		if (this._hasAutoEffect) {
			this._effectRange = this._json.autoRange * this._innerWidth || 0.12 * this._innerWidth;
		}

	}

	/**
	 * Get the realtive position. This is tranformed when part of a zone
	 * @returns geometry.Vector2
	 */
	public getRelativePosition(): geometry.Vector2 {
		if (this._sceneProps && this._sceneProps.zones && this._zone) {
			return this.getZoneXY(this._sceneProps.zones[this._zone]);
		}

		const relPoints = this._relativePointSettings();
		return {
			x: relPoints.x,
			y: relPoints.y
		};
	}

	private _getCurrentTexture(): PIXI.Texture | null {
		if (!this._settings.texture) {
			return null;
		}

		if (!this._settings.textureMobile) {
			return this._settings.texture;
		}

		return this._innerWidth > Config.getCurrentMobileSwitch() ? this._settings.texture : this._settings.textureMobile;
	}

	private _relativePointSettings(): geometry.Vector2 {
		let relX, relY;
		const mobileAt = this._json.mobileSizesAtWidth ? this._json.mobileSizesAtWidth : webglHome.getSwitchScreensLayoutAt();

		if (!this._json.relativeXMobile) {
			relX = this._json.relativeX;
		} else {
			relX = this._innerWidth <= mobileAt ? this._json.relativeXMobile : this._json.relativeX;
		}

		if (!this._json.relativeYMobile) {
			relY = this._json.relativeY;
		} else {
			relY = this._innerWidth <= mobileAt ? this._json.relativeYMobile : this._json.relativeY;
		}

		return { x: relX, y: relY };
	}

	/**
	 * get a item that us part of a zones X/Y
	 * @param zone
	 * @returns geometry.Vector2
	 */
	private getZoneXY(zone: any = null): geometry.Vector2 {
		const relPoints = this._relativePointSettings();
		let x = zone.x + relPoints.x * zone.scaleBump * zone.w;

		if (this._zoneCropXShift) {
			x += (1 - zone.cropRatio) * this._zoneCropXShift * zone.w;
		}

		let scalePumpH = zone.scaleBump && zone.scaleBump > 1 ? 1 + (zone.scaleBump - 1) / 5 : 1;
		scalePumpH = 1;
		let y = zone.y + relPoints.y * scalePumpH * zone.h;

		if (this._zoneYSizeShift) {
			y += this._zoneYSizeShift * zone.sizeDiffRel;
		}

		return {
			x: x,
			y: y
		};
	}

	//Global tick to update animation:
	public tick(
		xProgressRatio: number,
		containerX: number,
		mouseYRatio: number,
		mouseXRatio: number,
		mouseIsDown: boolean = false,
		velocityRatio: number = 0,
		instant: boolean = false,
		mouseCords: any = null,
		mothPosition: any = null
	): void {
		//Deal with inernia on offset (used for things particle repelling)

		//Process X using the virtual containerX poosition
		//Note container is not actually moved on the x, just the items. This allows each to have their own inernia/ease powers

		//If positoned are zoned then translate the local zone cords set in config for the item
		//Zones are just virual boundaries used to size the content areas:


		let zone;

		if (this._sceneProps && this._sceneProps.zones && this._zone) {
			zone = this._sceneProps.zones[this._zone];

			let zoneXY = this.getZoneXY(zone);

			if (!this._animatingToWork) {
				this._targetPosition.setRelativePosition({
					x: zoneXY.x,
					y: zoneXY.y
				});
			}

			this._displayScale = this._sceneProps.zones[this._zone].scale;

			if (this._zoneCropScaleShift) {
				this._displayScale += (1 - zone.cropRatio) * this._zoneCropScaleShift;
			}

			//Items can also be hidden:
			if (this._zoneCropHide) {
				this._sprite.visible = zone.cropRatio > this._zoneCropHide;
			}
		}

		if (this.id === 'intro' && device.innerWidth < 600) {
			this._displayScale = 0.38;
		}

		if (this.id === 'systems-block-move') {
			this._yAnimate += 0.5 * this._yAnimateDirection;
			if (this._yAnimate > 50) {
				this._yAnimate = 50;
				this._yAnimateDirection = -1;
			} else if (this._yAnimate < -50) {
				this._yAnimate = -50;
				this._yAnimateDirection = 1;
			}
		}

		this._setParallaxEaseMultipler();
		this._setEasePowers(mouseIsDown, instant);
		this._calculateParallaxDragOffsetX(xProgressRatio, this._containerWidth);

		if (this._repellActive) {
			this._processRepell(mouseCords, mothPosition);
		}

		//Get mouseOffsetX to add parallax
		//Use the _containerHeight value

		//Only process mouse parallax on mouse (as its too extreme on touch actions)
		if (!device.isPortraitDevice) {
			if (!globals.lastCordsFromTouch) {
				this._offsetTargetY =
					-1 * (mouseYRatio * (this._containerHeight * (this.parallaxPowerMouse / this._easePowers.y)));

				this._mouseOffsetX =
					this._mouseOffsetX + (this._mouseOffsetTargetX - this._mouseOffsetX) / this._easePowers.mouse;
				this._offsetY = this._offsetY + (this._offsetTargetY - this._offsetY) / this._easePowers.mouse;
				this._mouseOffsetTargetX = -1 * (mouseXRatio * (this._containerHeight * (this.parallaxPowerMouse / 2)));
			} else {
				this._mouseOffsetX = this._offsetY = this._mouseOffsetTargetX = this._offsetTargetY = 0;
			}
		}

		//Set the offset factors (drag, mouseX, repell)
		this._offsetX = containerX + this._dragOffsetX + this._mouseOffsetX + this._repell.moveX;

		let mobileSwitch = Config.getCurrentMobileSwitch();
		let processScreensSqueeze =
			device.innerWidth > mobileSwitch &&
			device.innerHeight > device.innerWidth;

		processScreensSqueeze = false;

		if (processScreensSqueeze && this.id == 'screensTVBottomLeft') {
			let squeezeAt = 1400;

			if (device.innerWidth < squeezeAt) {
				let move = 80;
				let range = squeezeAt - mobileSwitch;
				let ratio = 1 - (device.innerWidth - mobileSwitch) / range;
				this._offsetX += ratio * move;
			}
		} else if (processScreensSqueeze && this.id == 'screensTVTopRight') {
			let squeezeAt = 1400;

			if (device.innerWidth < squeezeAt) {
				let move = 50;
				let range = squeezeAt - mobileSwitch;
				let ratio = 1 - (device.innerWidth - mobileSwitch) / range;
				this._offsetX -= ratio * move;
			}
		}

		//Set the new target position passed on time line position
		this._targetPosition.update(
			this._offsetX,
			this._offsetY + this._repell.moveY + this._repell.moveYMoth + this._yAnimate
		);

		//Run through list of modifier to effect the target (attract / repell / etc)
		//------------------------------------------------------------------------------------

		//Deal with partcile attracting logic
		if (this._particleAttract) {
			//Pull the particle towards the mouse
			this._particleAttract.tick(
				mouseCords,
				this._targetPosition.absoluteX,
				containerX,
				this._targetPosition.absoluteY,
				this._mouseOffsetX,
				this._dragOffsetX
			);
			this._targetPosition.increase(this._particleAttract.getX(), this._particleAttract.getY());
		}

		if (this._particleAttract) {
			let target = this._particleAttractRepell.retarget(mouseCords, {
				x: this._targetPosition.x,
				y: this._targetPosition.y
			});

			this._targetPosition.setPosition(target);
		}

		this._x = this._x + (this._targetPosition.x - this._x) / this._easePowers.x;

		//Effect Y postion. Effect by mouse position and the acceloremeter:
		//Scale effect (For whoozy-ness) ----------------------------------------:

		//If autosclaing is on:
		if (this._hasAutoEffect) {
			if (this._json.autoScale) {
				if (this.department === DepartmentType.SCREENS && this._x < this._effectRange) {
					this._scaleTarget =
						this._autoStartScale +
						(this._finalScale - this._autoStartScale) *
							Math.max(0, Math.min(this._finalScale, this._x / this._effectRange));
				} else if (this.department === DepartmentType.SPACES && this._x > this._innerWidth - this._effectRange) {
					let realRange = this._effectRange;
					let realWidth = this._innerWidth;
					if (this._startRangeAt) {
						let rangeStartPx = this._innerWidth * this._startRangeAt;
						realRange = this._effectRange - rangeStartPx;
						realWidth = this._innerWidth - rangeStartPx;
					}

					this._scaleTarget =
						this._autoStartScale +
						(this._finalScale - this._autoStartScale) *
							Math.max(0, Math.min(this._finalScale, (realWidth - this._x) / realRange));
				}
			}

			if (this._json.autoAlpha) {
				if (this.department === DepartmentType.SCREENS && this._x < this._effectRange) {
					this._alpha =
						this._autoStartAlpha + (1.0 - this._autoStartAlpha) * Math.max(0, Math.min(1, this._x / this._effectRange));
				} else if (this.department === DepartmentType.SPACES && this._x > this._innerWidth - this._effectRange) {
					this._alpha =
						this._autoStartAlpha +
						(1.0 - this._autoStartAlpha) * Math.max(0, Math.min(1, (this._innerWidth - this._x) / this._effectRange));
				}

				this._sprite.alpha = this._alpha;
			}

			if (this._json.autoRotate) {
				if (this.department === DepartmentType.SCREENS && this._x < this._effectRange) {
					this._rotationTarget =
						this._finalRotation +
						this._autoStartRotation * Math.max(0, Math.min(1, (this._effectRange - this._x) / this._effectRange));
				} else if (this.department === DepartmentType.SPACES && this._x > this._innerWidth - this._effectRange) {
				}
			}
		}

		this._scale = this._scale + (this._scaleTarget - this._scale) / this._easePowers.scale;

		//Rotation: -------------------------------------------------------------
		this._rotation = this._rotation + (this._rotationTarget - this._rotation) / this._easePowers.rotation;

		//Set the final positions ----------------------------------------------
		this._sprite.position.set(this._x, this._targetPosition.y);


		const scaleTweak =
			this._json.mobileScaleIncrease && this._innerWidth < webglHome.getSwitchScreensLayoutAt()
				? this._json.mobileScaleIncrease
				: 1;
		this._sprite.scale.set(
			scaleTweak * this._scale * this._displayScale,
			scaleTweak * this._scale * this._displayScale
		);

		this._sprite.rotation = this._rotation;

		if (this.id === 'intro') {
			this._scale = 0.6;
			this._sprite.scale.set(this._scale, this._scale);
		}


		this._hitTestable = false;
		if ((this.id === 'systems-block' || this.id === 'systems-block-move') && this.sprite.alpha > 0) {
			this._hitTestable = true;
		}

		if (this._hitTestable) {
			this._addToHitTest();
		}

		if (this._targetable) {
			ParticleTargets.add(this.id, this._sprite.x, this._sprite.y);
		}

		if (this._spriteBindable) {
			ParallaxItemTargets.add(this.id, this._sprite);
		}

		if (this._json.hideAtWidth) {
			this._sprite.visible = this._innerWidth > this._json.hideAtWidth;
		}

		if (this._json.textureMobile) {
			let texture = this._getCurrentTexture();
			if (this._currentTexture != texture) {
				this._currentTexture = texture;
				this._sprite.texture = this._currentTexture;
			}
		}
	}

	/**
	 * Animate off for work section
	 * @param {number} moveY
	 * @param {number} delay
	 */
	public animateToWork(moveY: number, delay: number = 0, instantAtStart = false): void {
		if (this._particleAttractRepell) {
			this._particleAttractRepell.setActive(false);
		}

		if (instantAtStart) {
			this._targetPosition.setRelativePosition({
				x: this._targetPosition.relativePosition.x,
				y: this._targetPosition.relativePosition.y - moveY
			});
			return;
		}

		let cords = this._targetPosition.relativePosition;
		gsap.TweenMax.to(cords, Config.toWorkTime, {
			y: cords.y - 1,
			ease: Config.toWorkEase,
			onUpdate: () => {
				this._targetPosition.setRelativePosition({
					y: cords.y,
					x: this._targetPosition.relativePosition.x
				});
				this._targetPosition.resize(this._containerWidth, this._containerHeight);
			}
		});

		this._animatingToWork = true;

	}

	//Set when deep linked to section, to stop zone updating:
	public setAnimatingToWork(value: boolean) {
		this._animatingToWork = value;
	}

	/**
	 * Animate back from work. Note reset manages setting start positons
	 * @param {number} delay
	 */
	public animateFromWork(delay: number = 1.0): void {

		let cords = this._targetPosition.relativePosition;
		let targetPosition = this.getRelativePosition();

		gsap.TweenMax.to(cords, Config.toWorkTime, {
			y: targetPosition.y,
			ease: Config.toWorkEase,
			onUpdate: () => {
				this._targetPosition.setRelativePosition({
					y: cords.y,
					x: targetPosition.x
				});
				this._targetPosition.resize(this._containerWidth, this._containerHeight);
			}
		});
	}

	/** Remove any animations / interval on reset */
	public resetIntervalsAndAnimations(): void {
		if (this._tween) {
			this._tween.kill();
		}
	}

	/** General reset */
	public reset(): void {
		this._sprite.y = this._targetPosition.absoluteY + this._offsetY;
	}

	/**
	 * Reset when coming back from work. Tiles need to be put off screen. (X position is managed through normal updates)
	 * @param {number} offY
	 */
	public resetFromWork(offY: number): void {
		this._sprite.y = offY;
	}

	/** Set the timeline state for */
	public setTimelineState(state: TimelineStateType): void {
		if (this._particleAttract) {
			this._particleAttract.setTimelineState(state);
		}
	}

	public setParticleRepellCenter(center: Point): void {
		if (this._particleAttractRepell) {
			this._particleAttractRepell.setParticleRepellCenter(center);
		}
	}

	//PRIVATE
	//----------------------------------------------------------------------------------------------
	//----------------------------------------------------------------------------------------------
	//----------------------------------------------------------------------------------------------
	//----------------------------------------------------------------------------------------------

	//Calculate the parallax offset caused by draging left <> right:
	private _calculateParallaxDragOffsetX(progress: number, containerWidth: number): void {
		//Correct thing to allow for the fact the start/photoshop point of the timeline is center/0.5/home

		let zone = null;
		if (this._sceneProps && this._sceneProps.zones && this._zone) {
			zone = this._sceneProps.zones[this._zone];
		}

		if (zone && zone.parallax) {
			let affectiveWidth = this._innerWidth * (this._viewPortCount / 4);
			this._dragOffsetX = -1 * this.parallaxOffsetPower * (zone.parallax.rel * affectiveWidth);
			return;
		}

		if (this._parallaxOffsetBase != 0.5) {
			let effectRatio = progress - this._parallaxOffsetBase;
			this._dragOffsetX = effectRatio * this.parallaxOffsetPower * this._innerWidth * (this._viewPortCount / 4);
			return;
		}

		this._relativeParallaxX = this.relativeX - 0.5;
		//Translate this into the current progress
		this._relativeParallaxXToProgressRatio = this._relativeParallaxX - (progress - 0.5);
		//Set the offset as percentage of the container width (half the width because of the 0.5 logic).
		this._dragOffsetX =
			this._relativeParallaxXToProgressRatio *
			this.parallaxOffsetPower *
			(containerWidth / 4) *
			(this._viewPortCount / 3);
	}

	private _setEasePowers(mouseIsDown: boolean, instant: boolean = false): void {
		//When instant don't any ineria stuff. Just put at the correct state:
		if (instant) {
			this._easePowers.mouse = 1;
			this._easePowers.x = 1;
			this._easePowers.y = 1;
			this._easePowers.scale = 1;
			this._easePowers.rotation = 1;
			return;
		}

		this._easePowers.mouse = mouseIsDown ? 5 * this._parallaxEaseMultipler : 20 * this._parallaxEaseMultipler;
		this._easePowers.x = 1 * this._parallaxEaseMultipler;
		this._easePowers.y = 4 * this._parallaxEaseMultipler;
		this._easePowers.scale = 8 * this._parallaxEaseMultipler;
		this._easePowers.rotation = 2 * this._parallaxEaseMultipler;
	}


	/** Set the easeMultiple on each frame to reduce back to 1 */
	private _setParallaxEaseMultipler(): void {
		if (this._parallaxEaseMultipler > 1) {
			this._parallaxEaseMultipler -= 0.5;
		}
	}

	/** Process repell force */
	private _processRepell(mouseCords: any, mothCords: any): void {
		//If mouse cords from mouse then process repell
		if (!device.isPortraitDevice) {
			this._processRepellMouse(mouseCords);
		}
	}

	private _processRepellMouse(mouseCords: any): void {
		//Reworked from:
		//https://codepen.io/biblos/pen/KRJmey

		if (globals.lastCordsFromTouch) {
			this._repell.moveX = 0;
			this._repell.moveY = 0;
			return;
		}

		this._repell.distancex = mouseCords.x - this._x;
		this._repell.distancey = mouseCords.y - (this._absoluteY + this._offsetY);
		this._repell.distance = Math.sqrt(
			this._repell.distancex * this._repell.distancex + this._repell.distancey * this._repell.distancey
		);
		this._repell.powerx =
			((this._repell.distancex / this._repell.distance) * this._repell.magnet) / this._repell.distance;
		this._repell.powery =
			((this._repell.distancey / this._repell.distance) * this._repell.magnet) / this._repell.distance;

		let ratio = 0;
		if (this._repell.distance < this._distanceTolerence) {
			this._repell.moveXTarget = this._repell.powerx * -this._mouseRepellPower;
			this._repell.moveYTarget = this._repell.powery * -this._mouseRepellPower;
		} else {
			this._repell.moveXTarget = this._repell.moveYTarget = 0;
		}

		let easeP = this._mouseRepelEasePower + 100 * ratio;
		this._repell.moveX = this._repell.moveX + (this._repell.moveXTarget - this._repell.moveX) / easeP;
		this._repell.moveY = this._repell.moveY + (this._repell.moveYTarget - this._repell.moveY) / easeP;
	}

	private _addToHitTest() {
		ParticleHitTests.add(this._sprite.x, this._sprite.y, this._sprite.width, this._sprite.height, this._displayScale);
	}
}
